import { codigoTotpSchema, loginEquipeSchema } from '@mymaxx/shared';
import { Botao, CampoTexto } from '@mymaxx/ui';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ErroApi, authApi } from '../lib/api';
import { useSessao } from '../lib/sessao';

type Etapa = { tipo: 'credenciais' } | { tipo: 'totp'; tokenTemporario: string };

/** Login da equipe em duas etapas: e-mail + senha e, se o usuário tiver TOTP, o código do autenticador. */
export function Login() {
  const { accessToken, definir } = useSessao();
  const navegar = useNavigate();
  const local = useLocation();
  const destino = (local.state as { de?: string } | null)?.de ?? '/';

  const [etapa, setEtapa] = useState<Etapa>({ tipo: 'credenciais' });
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (accessToken) return <Navigate to={destino} replace />;

  async function enviarCredenciais(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    const dados = loginEquipeSchema.safeParse({ email, senha });
    if (!dados.success) {
      setErro(dados.error.issues[0]?.message ?? 'Dados inválidos');
      return;
    }
    setEnviando(true);
    try {
      const r = await authApi.login(dados.data);
      if (r.etapa === 'totp') {
        setEtapa({ tipo: 'totp', tokenTemporario: r.tokenTemporario });
      } else {
        definir(r);
        navegar(destino, { replace: true });
      }
    } catch (err) {
      setErro(err instanceof ErroApi ? err.mensagem : 'Falha inesperada');
    } finally {
      setEnviando(false);
    }
  }

  async function enviarCodigo(e: FormEvent) {
    e.preventDefault();
    if (etapa.tipo !== 'totp') return;
    setErro(null);
    const parse = codigoTotpSchema.safeParse(codigo);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Código inválido');
      return;
    }
    setEnviando(true);
    try {
      definir(await authApi.validarTotp(etapa.tokenTemporario, parse.data));
      navegar(destino, { replace: true });
    } catch (err) {
      const mensagem = err instanceof ErroApi ? err.mensagem : 'Falha inesperada';
      setErro(mensagem);
      // Token temporário expirou (5 min): volta para a 1ª etapa.
      if (err instanceof ErroApi && err.status === 401 && /expirada/i.test(mensagem)) {
        setEtapa({ tipo: 'credenciais' });
        setCodigo('');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-card border border-neutral-200 p-6 dark:border-neutral-800">
        <img src="/logo.png" alt="MyMAXX" className="mx-auto size-14" width={56} height={56} />
        <h1 className="mb-6 text-xl font-semibold">Acesso da equipe</h1>

        {etapa.tipo === 'credenciais' ? (
          <form className="space-y-4" onSubmit={enviarCredenciais} noValidate>
            <CampoTexto
              rotulo="E-mail"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <CampoTexto
              rotulo="Senha"
              type="password"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
            {erro && (
              <p role="alert" className="text-sm text-red-600">
                {erro}
              </p>
            )}
            <Botao type="submit" className="w-full" carregando={enviando}>
              Entrar
            </Botao>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={enviarCodigo} noValidate>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Abra o app autenticador no celular e digite o código de 6 dígitos.
            </p>
            <CampoTexto
              rotulo="Código do autenticador"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={7}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
            />
            {erro && (
              <p role="alert" className="text-sm text-red-600">
                {erro}
              </p>
            )}
            <Botao type="submit" className="w-full" carregando={enviando}>
              Confirmar
            </Botao>
            <button
              type="button"
              className="w-full text-center text-xs text-neutral-500 underline"
              onClick={() => {
                setEtapa({ tipo: 'credenciais' });
                setCodigo('');
                setErro(null);
              }}
            >
              Voltar
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
